import React, { useState } from "react";
import { Button, Card, DatePicker, Form, Input, message, Modal, Popconfirm, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import locale from "antd/es/date-picker/locale/nl_NL";

dayjs.extend(isBetween);

const Notifications = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const notifications = useSelector((state) => state.firestoreReducer.ordered.notifications);

  const openModal = (notification = null) => {
    setEditingNotification(notification);
    if (notification) {
      form.setFieldsValue({
        ...notification,
        startDateTime: dayjs.unix(notification.startDateTime),
        endDateTime: dayjs.unix(notification.endDateTime),
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingNotification(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        title: values.title,
        message: values.message,
        startDateTime: values.startDateTime.unix(),
        endDateTime: values.endDateTime.unix(),
        active: isNotificationActive(values.startDateTime, values.endDateTime),
      };

      if (editingNotification) {
        await firestore.collection("notifications").doc(editingNotification.id).update(data);
      } else {
        await firestore.collection("notifications").add(data);
      }

      message.success(t("notification_saved"));
      closeModal();
    } catch (error) {
      console.error("Error saving notification:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await firestore.collection("notifications").doc(id).delete();
      message.success(t("notification_deleted"));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const isNotificationActive = (start, end) => {
    const now = dayjs();
    return now.isBetween(dayjs(start), dayjs(end));
  };

  const getNotificationStatus = (notification) => {
    const now = dayjs();
    const start = dayjs.unix(notification.startDateTime);
    const end = dayjs.unix(notification.endDateTime);

    if (now.isBefore(start)) {
      return { text: t("inactive"), color: "default" };
    } else if (now.isAfter(end)) {
      return { text: t("inactive"), color: "default" };
    } else {
      return { text: t("active"), color: "green" };
    }
  };

  const columns = [
    {
      title: t("title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: t("message"),
      dataIndex: "message",
      key: "message",
      ellipsis: true,
    },
    {
      title: t("start_date_time"),
      dataIndex: "startDateTime",
      key: "startDateTime",
      render: (timestamp) => dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm"),
      sorter: (a, b) => a.startDateTime - b.startDateTime,
    },
    {
      title: t("end_date_time"),
      dataIndex: "endDateTime",
      key: "endDateTime",
      render: (timestamp) => dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: t("status"),
      key: "status",
      render: (_, record) => {
        const status = getNotificationStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="link" icon={<EditOutlined />} onClick={() => openModal(record)} />
          <Popconfirm
            title={t("delete_notification")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>{t("notifications_management")}</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          {t("add_notification")}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={notifications}
        rowKey="id"
        locale={{ emptyText: t("table_no_notifications") }}
      />

      <Modal
        title={editingNotification ? t("edit_notification") : t("add_notification")}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        okText={t("save")}
        cancelText={t("cancel")}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label={t("title")} rules={[{ required: true, message: t("required") }]}>
            <Input placeholder={t("title")} />
          </Form.Item>

          <Form.Item name="message" label={t("message")} rules={[{ required: true, message: t("required") }]}>
            <Input.TextArea rows={4} placeholder={t("message")} />
          </Form.Item>

          <Form.Item
            name="startDateTime"
            label={t("start_date_time")}
            rules={[{ required: true, message: t("required") }]}
          >
            <DatePicker showTime style={{ width: "100%" }} format="DD-MM-YYYY HH:mm" locale={locale} />
          </Form.Item>

          <Form.Item
            name="endDateTime"
            label={t("end_date_time")}
            rules={[
              { required: true, message: t("required") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue("startDateTime")) {
                    return Promise.resolve();
                  }
                  if (value.isAfter(getFieldValue("startDateTime"))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("end_date_must_be_after_start_date")));
                },
              }),
            ]}
          >
            <DatePicker showTime style={{ width: "100%" }} format="DD-MM-YYYY HH:mm" locale={locale} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Notifications;
